import { useModalLifecycle } from '../shared/useModalLifecycle'
import React, { useState } from 'react'
import {
  X,
  Video,
  ExternalLink,
  RefreshCw,
  Download,
  AlertCircle,
  Clock,
  Layers,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { ContributingSnap } from '../../types/sportsStats'

interface ContributingSnapsModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle?: string
  snaps: ContributingSnap[]
  loading: boolean
  onRecalculate?: () => void
  onExportCsv?: () => void
}

export const ContributingSnapsModal: React.FC<ContributingSnapsModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  snaps,
  loading,
  onRecalculate,
  onExportCsv,
}) => {
  const [expandedSnapId, setExpandedSnapId] = useState<string | null>(null)

  useModalLifecycle(isOpen, onClose)

  if (!isOpen) return null

  const toggleExpand = (id: string) => {
    setExpandedSnapId(expandedSnapId === id ? null : id)
  }

  const getResultBadgeClass = (res: string | null, isTd: boolean) => {
    if (isTd) return 'badge-red'
    switch (res) {
      case 'pass_complete':
        return 'badge-blue'
      case 'run':
        return 'badge-blue'
      case 'pass_incomplete':
        return 'badge-muted'
      case 'interception':
        return 'badge-red'
      case 'sack':
        return 'badge-gold'
      default:
        return 'badge-muted'
    }
  }

  const formatResultLabel = (res: string | null, isTd: boolean) => {
    if (isTd) return 'Touchdown'
    switch (res) {
      case 'pass_complete':
        return 'Pase Completo'
      case 'run':
        return 'Carrera'
      case 'pass_incomplete':
        return 'Incompleto'
      case 'interception':
        return 'Intercepción'
      case 'sack':
        return 'Sack'
      default:
        return res || 'N/D'
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 15, 30, 0.65)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '850px',
          maxHeight: '88vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          borderRadius: 'var(--radius-lg, 12px)',
          overflow: 'hidden',
          backgroundColor: 'var(--surface-card, #ffffff)',
          border: '1px solid var(--surface-border, #e2e8f0)',
        }}
      >
        {/* Cabecera del modal */}
        <div
          style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid var(--surface-border, #e2e8f0)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem',
            backgroundColor: 'var(--surface-header, #f8fafc)',
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <Layers size={18} style={{ color: 'var(--brand-blue, #1e3a8a)' }} />
              <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0, color: 'var(--text-primary, #0f172a)' }}>
                {title}
              </h2>
            </div>
            {subtitle && (
              <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-secondary, #64748b)' }}>
                {subtitle}
              </p>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {onExportCsv && (
              <button
                onClick={onExportCsv}
                className="btn btn-secondary"
                title="Exportar estos snaps a CSV"
                style={{ padding: '0.4rem 0.65rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
              >
                <Download size={14} />
                <span>Exportar CSV</span>
              </button>
            )}

            {onRecalculate && (
              <button
                onClick={onRecalculate}
                className="btn btn-secondary"
                title="Actualizar y recalcular datos"
                disabled={loading}
                style={{ padding: '0.4rem 0.65rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
              >
                <RefreshCw size={14} className={loading ? 'spin' : ''} />
                <span>Recalcular</span>
              </button>
            )}

            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-secondary, #64748b)',
                padding: '0.4rem',
                borderRadius: '6px',
              }}
              title="Cerrar"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Resumen de muestra */}
        <div
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: 'rgba(30, 58, 138, 0.04)',
            borderBottom: '1px solid var(--surface-border, #e2e8f0)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.82rem',
            flexShrink: 0,
          }}
        >
          <span>
            Total de snaps que contribuyen a esta métrica: <strong>{snaps.length}</strong>
          </span>
          <span style={{ color: 'var(--text-secondary, #64748b)' }}>
            Trazabilidad directa según contrato deportivo v0
          </span>
        </div>

        {/* Lista de snaps con scroll */}
        <div
          style={{
            padding: '1rem',
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-secondary, #64748b)' }}>
              <RefreshCw size={24} className="spin" style={{ marginBottom: '0.5rem' }} />
              <div>Cargando detalle de jugadas contribuyentes...</div>
            </div>
          ) : snaps.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'var(--text-secondary, #64748b)' }}>
              <AlertCircle size={28} style={{ marginBottom: '0.5rem', opacity: 0.6 }} />
              <div>No se encontraron snaps que cumplan este filtro.</div>
            </div>
          ) : (
            snaps.map((snap) => {
              const isExpanded = expandedSnapId === snap.id

              return (
                <div
                  key={snap.id}
                  style={{
                    border: '1px solid var(--surface-border, #e2e8f0)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--surface-card, #ffffff)',
                    overflow: 'hidden',
                    transition: 'box-shadow 0.15s ease',
                    flexShrink: 0,
                    flexGrow: 0,
                    minHeight: 'fit-content',
                    boxSizing: 'border-box',
                    width: '100%',
                  }}
                >
                  {/* Fila principal del snap */}
                  <div
                    onClick={() => toggleExpand(snap.id)}
                    style={{
                      padding: '0.75rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      gap: '0.75rem',
                      backgroundColor: isExpanded ? 'rgba(0,0,0,0.02)' : 'transparent',
                      flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem 0.75rem', flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
                      <span
                        style={{
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          color: 'var(--brand-blue, #1e3a8a)',
                          minWidth: '65px',
                        }}
                      >
                        Snap #{snap.sequence}
                      </span>

                      <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {snap.match_title}
                      </span>

                      <span className={`badge ${getResultBadgeClass(snap.primary_result, snap.is_touchdown)}`}>
                        {formatResultLabel(snap.primary_result, snap.is_touchdown)}
                      </span>

                      <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #64748b)' }}>
                        {snap.down ? `${snap.down}º Down` : 'Down N/D'} • Yardas {snap.yard_line_start ?? '?'}{' '}
                        → {snap.yard_line_end ?? '?'}{' '}
                        (<strong>{snap.net_yards !== null ? `${snap.net_yards > 0 ? `+${snap.net_yards}` : snap.net_yards} yds` : 'N/D'}</strong>)
                      </span>

                      {snap.had_lateral && (
                        <span className="badge badge-gold" title="Jugada con lateral">
                          Lateral (Yds N/D)
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {snap.video_url && (
                        <a
                          href={snap.video_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: 'var(--brand-red, #d9232c)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            fontSize: '0.78rem',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: 'rgba(217, 35, 44, 0.08)',
                            textDecoration: 'none',
                          }}
                          title="Abrir vídeo de la jugada en Google Drive"
                        >
                          <Video size={13} />
                          <span>Vídeo {snap.video_timestamp_mark ? `(${snap.video_timestamp_mark})` : ''}</span>
                          <ExternalLink size={11} />
                        </a>
                      )}

                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>

                  {/* Detalle expandible */}
                  {isExpanded && (
                    <div
                      style={{
                        padding: '0.75rem 1rem',
                        borderTop: '1px solid var(--surface-border, #e2e8f0)',
                        backgroundColor: '#f8fafc',
                        fontSize: '0.82rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.6rem',
                      }}
                    >
                      {/* Concepto / Jugada / Llamada defensiva */}
                      <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <div>
                          <span style={{ color: 'var(--text-secondary, #64748b)' }}>Llamada / Concepto: </span>
                          <strong>{snap.concept_or_call || 'No registrado (N/D)'}</strong>
                        </div>
                        {snap.play_name && (
                          <div>
                            <span style={{ color: 'var(--text-secondary, #64748b)' }}>Jugada: </span>
                            <strong>{snap.play_name}</strong>
                          </div>
                        )}
                        {snap.passer_name && (
                          <div>
                            <span style={{ color: 'var(--text-secondary, #64748b)' }}>Lanzador real: </span>
                            <strong>{snap.passer_name}</strong>
                          </div>
                        )}
                        {snap.receiver_name && (
                          <div>
                            <span style={{ color: 'var(--text-secondary, #64748b)' }}>Receptor: </span>
                            <strong>{snap.receiver_name}</strong>
                          </div>
                        )}
                        {snap.tackler_name && (
                          <div>
                            <span style={{ color: 'var(--text-secondary, #64748b)' }}>Defensor / Placador: </span>
                            <strong>{snap.tackler_name}</strong>
                          </div>
                        )}
                      </div>

                      {/* Quinteto real de 5 participantes inmutables */}
                      <div>
                        <span style={{ color: 'var(--text-secondary, #64748b)', display: 'block', marginBottom: '0.25rem' }}>
                          Quinteto real en campo (5 jugadores históricos inmutables):
                        </span>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {snap.participants.length > 0 ? (
                            snap.participants.map((p, idx) => (
                              <span
                                key={idx}
                                style={{
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #cbd5e1',
                                  padding: '0.2rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.78rem',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.3rem',
                                }}
                              >
                                <span style={{ fontWeight: 700, color: 'var(--brand-blue, #1e3a8a)' }}>
                                  #{p.dorsal}
                                </span>
                                <span>{p.name}</span>
                                <span style={{ color: '#64748b', fontSize: '0.72rem' }}>({p.position})</span>
                              </span>
                            ))
                          ) : (
                            <span style={{ color: '#94a3b8' }}>Sin participantes registrados</span>
                          )}
                        </div>
                      </div>

                      {/* Eventos secundarios del snap */}
                      {snap.events.length > 0 && (
                        <div>
                          <span style={{ color: 'var(--text-secondary, #64748b)', display: 'block', marginBottom: '0.25rem' }}>
                            Eventos del snap:
                          </span>
                          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                            {snap.events.map((ev, eIdx) => (
                              <span
                                key={eIdx}
                                className="badge badge-muted"
                                style={{ fontSize: '0.72rem', padding: '0.15rem 0.45rem' }}
                              >
                                {ev.type}
                                {ev.actor_name ? `: ${ev.actor_name}` : ''}
                                {ev.target_name ? ` → ${ev.target_name}` : ''}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Referencias de vídeo y offset */}
                      {snap.video_segment_id && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            paddingTop: '0.35rem',
                            borderTop: '1px dashed #cbd5e1',
                            fontSize: '0.76rem',
                            color: '#475569',
                          }}
                        >
                          <Clock size={13} />
                          <span>Segmento: <strong>{snap.video_segment_id}</strong></span>
                          {snap.video_timestamp_mark && (
                            <span>Marca: <strong>{snap.video_timestamp_mark}</strong></span>
                          )}
                          {snap.video_offset_seconds !== null && (
                            <span>Offset: <strong>+{snap.video_offset_seconds}s</strong></span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

        {/* Pie del modal */}
        <div
          style={{
            padding: '0.85rem 1.5rem',
            borderTop: '1px solid var(--surface-border, #e2e8f0)',
            backgroundColor: 'var(--surface-header, #f8fafc)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            gap: '1rem',
          }}
        >
          <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary, #64748b)' }}>
            Mostrando <strong>{snaps.length}</strong> {snaps.length === 1 ? 'jugada' : 'jugadas'} con trazabilidad completa
          </span>
          <button onClick={onClose} className="btn btn-secondary">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}
